import styles from './processing-status.module.css';
import ProcessingStatusModal from './processing-status-modal';
import { IconLoader, Spinner, Tag } from '@SBGSports/catapult-core';
import { useTranslations } from 'i18n';
import {
    ActivityStatusResponse,
    AthleteStatusResponse,
    getStatusVariant,
    isActivityProcessing,
} from './activity-status-util';
import { useWindowSize } from 'shared';
import { useProcessingStatusStore } from 'shared/state/processingStatusStore';
import mixpanel from 'mixpanel-browser';
import { EVENTS } from '../analytics/analytics-events';
import { useState } from 'react';

export type ActivityAthletesStatus = { activityId: string; athleteStatuses: AthleteStatusResponse[] };

interface ProcessingStatusButtonProps {
    activityStatuses: ActivityStatusResponse[];
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
}

const ProcessingStatusButton: React.FC<ProcessingStatusButtonProps> = ({
    activityStatuses,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
}: ProcessingStatusButtonProps) => {
    const { width } = useWindowSize();
    const isMobile = width < 768;

    const { __ } = useTranslations();
    const {
        openModal: showProcessingModal,
        setOpenModal: setShowProcessingModal,
        setFocusedActivityId,
    } = useProcessingStatusStore();

    const openModal = () => {
        setShowProcessingModal(true);
        // prevent close button from getting focus on opening the modal
        setTimeout(() => {
            (document.querySelector('[aria-label="Close"]') as HTMLElement)?.blur();
        }, 0);
    };
    const closeModal = () => {
        setShowProcessingModal(false);
        setFocusedActivityId('');
        // prevent button from re-gaining focus on closing the modal
        setTimeout(() => {
            document.getElementById('processing-status-button')?.blur();
        }, 0);
    };
    // we exclude the statuses of those activities that we cannot find (assuming deleted)
    const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
    const filteredStatuses = activityStatuses?.filter((s) => !excludedIds.has(s.activityId));

    const anyErrorStatus = Boolean(filteredStatuses?.find((a) => a.status.includes('error')));
    // frontend-computed "error" variant
    const anyProcessedWithMissingStatus = Boolean(
        filteredStatuses?.find((a) => {
            const statusVariant = getStatusVariant(a.status);
            return statusVariant?.startsWith('processedWithMissing-');
        }),
    );
    const anyErrorVariant = anyErrorStatus || anyProcessedWithMissingStatus;
    const anyActivityProcessing = Boolean(filteredStatuses?.find((a) => isActivityProcessing(a.status)));
    return (
        <>
            <button
                className={`relative ${isMobile ? 'ml-2' : 'mr-2'} bg-transparent`}
                onClick={openModal}
                tabIndex={0}
            >
                <Tag
                    className={`px-2 relative ${anyActivityProcessing ? 'py-0.5' : 'py-1'}`}
                    color={anyActivityProcessing ? 'warning' : 'primary'}
                    label={
                        anyActivityProcessing
                            ? __('data_processing.activity_processing')
                            : __('data_processing.processing_history')
                    }
                    id="processing-status-button"
                    handleClick={() => {
                        mixpanel.track(EVENTS.processingStatus.modalOpened);
                        openModal();
                    }}
                    leftComponent={{
                        element: (
                            <Spinner
                                size="xs"
                                color="primary"
                                className="mr-1.5"
                                id="processing-status-button-spinner"
                            />
                        ),
                        condition: anyActivityProcessing,
                    }}
                />
                {/* absolutely positioned icon seems to work better not used as Tag rightIcon prop. dot for white bg */}
                {anyErrorVariant && (
                    <div className={styles['dot']}>
                        <IconLoader
                            iconName="IconErrorWarning"
                            size="sm"
                            className="fill-system-error opacity-100 z-10 absolute"
                            style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '-2px',
                            }}
                        />
                    </div>
                )}
            </button>
            <ProcessingStatusModal
                showProcessingModal={showProcessingModal}
                onClose={closeModal}
                setExcludedIds={setExcludedIds}
                filteredStatuses={filteredStatuses}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
            />
        </>
    );
};

export default ProcessingStatusButton;
